import React, { useState, useEffect } from 'react';
import {
    Box, Flex, Heading, Text, Badge, Button,
    useColorModeValue, useToast, Tooltip, Spinner,
    Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
    Grid, GridItem, Tag, TagLabel, TagLeftIcon
} from '@chakra-ui/react';
import { MdAdd, MdCheckCircle, MdCancel, MdLink, MdOpenInNew, MdRefresh } from 'react-icons/md';
import { SiGooglesheets } from 'react-icons/si';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../../axios-client';
import { useStateContext } from '../../../../context/ContextProvider';

export default function Gestion_GoogleSheet_Modify() {
    const { user, currentBusiness } = useStateContext();
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(null);
    const [toggling, setToggling] = useState(false);

    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [pendingConfigId, setPendingConfigId] = useState(null);

    const toast = useToast();
    const navigate = useNavigate();

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const subText = useColorModeValue('gray.500', 'gray.400');
    const connectedBg = useColorModeValue('green.50', 'green.900');
    const hubBg = useColorModeValue('gray.50', 'gray.800');

    const loadConfigurations = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const response = await axiosClient.get(`/api/client-sheets/list/${user.id}/`);
            if (response.data.success) {
                // Filter configurations by current business
                const filtered = response.data.configurations.filter(
                    c => c.business_id === (currentBusiness?.id || null)
                );
                setConfigurations(filtered);
            }
        } catch (error) {
            toast({
                description: 'Erreur lors du chargement des configurations',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        loadConfigurations();
    }, [user?.id, currentBusiness?.id]);

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const response = await axiosClient.post(`/api/client-sheets/create/${user.id}/`, {
                sheet_name: 'Modification Commandes',
                sheet_url: '',
                business_id: currentBusiness?.id || null
            });

            if (response.data.success) {
                toast({
                    description: 'Connexion créée avec succès !',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });

                // On récupère la configuration créée et on redirige
                const newConfig = response.data.configuration || {
                    ...response.data,
                    sheet_name: 'Modification Commandes'
                };
                navigate('/modifier-commandes-sheet/documentation', { state: { config: newConfig } });
            } else {
                toast({
                    description: response.data.error || 'Erreur lors de la création',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            }
        } catch (err) {
            toast({
                description: 'Erreur de connexion',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setConnecting(false);
        loadConfigurations();
    };

    const handleDisconnect = async () => {
        if (!pendingConfigId) return;
        setDisconnecting(pendingConfigId);
        try {
            // Utiliser l'endpoint de suppression ou de désactivation si existant. 
            // Ici on va d'abord vérifier si on doit juste le cacher ou le supprimer. 
            // Comme le backend de suppression de configuration n'était pas précisé, on peut juste appeler l'API de suppression.
            await axiosClient.delete(`/api/client-sheets/${pendingConfigId}/delete/`);
            toast({
                description: 'Connexion supprimée.',
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
            loadConfigurations();
        } catch (err) {
            toast({
                description: 'Erreur lors de la suppression.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setDisconnecting(null);
        onConfirmClose();
        setPendingConfigId(null);
    };

    const handleToggleActive = async (configId) => {
        setToggling(true);
        try {
            const response = await axiosClient.post(`/api/client-sheets/${configId}/toggle-active/`);
            if (response.data.success) {
                toast({
                    description: response.data.message || 'Statut mis à jour avec succès.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });
                loadConfigurations();
            } else {
                toast({
                    description: response.data.error || 'Erreur lors de la mise à jour du statut.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            }
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Erreur de connexion lors de la modification du statut.';
            toast({
                description: errMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setToggling(false);
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="300px">
                <Spinner size="xl" color="teal.400" />
            </Flex>
        );
    }

    const isConnected = configurations.length > 0;
    const conn = isConnected ? configurations[0] : null; // On gère 1 configuration principale pour simplifier
    const isActive = conn?.is_active;

    return (
        <Box bg={hubBg} minH="100vh" p={{ base: 4, md: 8 }}>
            <Flex
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                mb={8}
                gap={4}
            >
                <Box>
                    <Heading size="lg" mb={1}>
                        🔗 Modifier via Google Sheet
                    </Heading>
                    <Text color={subText} fontSize="sm">
                        Connectez votre Google Sheet pour modifier automatiquement les statuts de vos commandes.
                    </Text>
                </Box>
                <Button size="sm" leftIcon={<MdRefresh />} onClick={loadConfigurations} variant="outline">
                    Actualiser
                </Button>
            </Flex>

            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
                <GridItem>
                    <Box
                        bg={isConnected ? connectedBg : cardBg}
                        border="2px solid"
                        borderColor={isConnected ? 'teal.300' : borderC}
                        rounded="2xl"
                        p={6}
                        shadow={isConnected ? 'md' : 'sm'}
                        transition="all 0.2s"
                        _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                        position="relative"
                    >
                        {isConnected && (
                            <Badge
                                position="absolute"
                                top={3}
                                right={3}
                                colorScheme={isActive ? 'green' : 'red'}
                                rounded="full"
                                px={2}
                            >
                                {isActive ? 'Actif' : 'Inactif'}
                            </Badge>
                        )}

                        <Flex align="center" gap={3} mb={4}>
                            <Box>
                                <SiGooglesheets size={36} color="#34A853" />
                            </Box>
                            <Box>
                                <Text fontWeight="bold" fontSize="lg">
                                    Google Sheets
                                </Text>
                                <Badge colorScheme="teal" fontSize="xs">
                                    google_sheets_modify
                                </Badge>
                            </Box>
                        </Flex>

                        <Text fontSize="sm" color={subText} mb={5}>
                            Modifiez les statuts (confirmation, stock, livraison) directement depuis votre feuille de calcul.
                        </Text>

                        {isConnected && (
                            <Flex gap={4} mb={4} fontSize="xs" color={subText}>
                                <Box>
                                    <Text fontWeight="semibold">{conn.total_orders_modified || 0}</Text>
                                    <Text>commandes modifiées</Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold">
                                        {conn.last_modify
                                            ? new Date(conn.last_modify).toLocaleDateString()
                                            : '-'}
                                    </Text>
                                    <Text>dernière modification</Text>
                                </Box>
                            </Flex>
                        )}

                        <Flex gap={2} flexWrap="wrap">
                            {!isConnected ? (
                                <Button
                                    size="sm"
                                    colorScheme="teal"
                                    leftIcon={<MdAdd />}
                                    isLoading={connecting}
                                    onClick={handleConnect}
                                    w="full"
                                >
                                    Connecter
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        colorScheme="teal"
                                        leftIcon={<MdOpenInNew />}
                                        onClick={() =>
                                            navigate('/modifier-commandes-sheet/documentation', { state: { config: conn } })
                                        }
                                        flex={1}
                                    >
                                        Voir détails
                                    </Button>
                                    <Button
                                        size="sm"
                                        colorScheme={isActive ? "yellow" : "green"}
                                        leftIcon={isActive ? <MdCancel /> : <MdCheckCircle />}
                                        isLoading={toggling}
                                        onClick={() => handleToggleActive(conn.id)}
                                    >
                                        {isActive ? "Désactiver" : "Activer"}
                                    </Button>
                                    <Tooltip label="Déconnecter">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            colorScheme="red"
                                            onClick={() => {
                                                setPendingConfigId(conn.id);
                                                onConfirmOpen();
                                            }}
                                        >
                                            <MdLink />
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        </Flex>
                    </Box>
                </GridItem>
            </Grid>

            {/* Confirmation de déconnexion */}
            <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirmer la déconnexion</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            Êtes-vous sûr de vouloir supprimer la connexion Google Sheets Modification ?
                            Le webhook actuel cessera de fonctionner.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button
                            colorScheme="red"
                            isLoading={!!disconnecting}
                            onClick={handleDisconnect}
                        >
                            Supprimer
                        </Button>
                        <Button variant="ghost" onClick={onConfirmClose}>
                            Annuler
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}